// src/lib/opportunity/proposalTextSections.ts
//
// Section keys + hard-coded defaults for proposal text overrides
// (opp engine iter 2 slice C). The defaults here are the strings the
// PDF sections used to inline directly — we lift them to a single
// source of truth so the editor's "Default" placeholder shows the same
// text the customer will see when there's no override.

// NOTE: relative import (not `@/...`) so this module compiles under
// both tsconfigs — `tsconfig.app.json` (browser, alias-aware) and
// `tsconfig.node.json` (server / vercel-types, no `@/` alias). Same
// pattern as `calc.ts`.
import type { OpportunityFormatKey } from "../../types/opportunity.js";

/**
 * Wildcard `product_type` sentinel — matches every `product_type` that
 * resolves to the given `format_key` per `formatRegistry.ts`. Mirrors
 * the SQL DEFAULT in `docs/sql/opp_proposal_text_overrides_v1.sql`.
 */
export const PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD = "*";

export const PROPOSAL_TEXT_SECTION_KEYS = [
  "cover_letter_body",
  "cost_breakdown_intro",
  "cost_breakdown_footer",
  "payment_schedule_intro",
  "payment_schedule_footer",
  "intent_to_purchase_preamble",
  "intent_to_purchase_signature_block",
] as const;

export type ProposalTextSectionKey = (typeof PROPOSAL_TEXT_SECTION_KEYS)[number];

export const PROPOSAL_TEXT_SECTION_LABELS: Record<ProposalTextSectionKey, string> = {
  cover_letter_body: "Cover letter body",
  cost_breakdown_intro: "Cost breakdown intro",
  cost_breakdown_footer: "Cost breakdown footer",
  payment_schedule_intro: "Payment schedule intro",
  payment_schedule_footer: "Payment schedule footer",
  intent_to_purchase_preamble: "Intent to purchase preamble",
  intent_to_purchase_signature_block: "Intent to purchase signature block",
};

export const DEFAULT_COST_BREAKDOWN_FOOTER =
  "All values are in USD. Pricing valid for 30 days from generation date. Changes to scope after acceptance may trigger a new revision.";

export const DEFAULT_PAYMENT_SCHEDULE_FOOTER =
  "Wire instructions and acceptable payment methods will be supplied with the deposit invoice. Late payments may delay delivery.";

/**
 * Maximum body length per override row. Generous (~8KB) so a verbose
 * org can write a multi-paragraph cover letter, but bounded so the
 * editor + the PDF page don't fall over on a runaway paste.
 */
export const PROPOSAL_TEXT_MAX_LEN = 8000;

/**
 * Hard-coded defaults shown as placeholder copy in the editor and used
 * by the PDF when no override row exists. Default copy is format-aware:
 * the generic payment-schedule intro differs from red_iron because the
 * generic format only has two milestones (deposit + balance on
 * delivery).
 *
 * The strings are the exact copy the prior inlined PDF sections used;
 * lifting them here lets the editor render the same text in its
 * placeholder so reps can see "what they'd be replacing" before they
 * type anything.
 */
export function getProposalTextDefault(
  formatKey: OpportunityFormatKey,
  sectionKey: ProposalTextSectionKey,
): string {
  switch (sectionKey) {
    case "cover_letter_body":
      // Cover letter has always been generic across formats.
      return [
        "Thank you for the opportunity to put this proposal together.",
        "The following pages cover the cost breakdown, the payment schedule, and an intent-to-purchase signature block.",
        "We've prepared this package to keep the scope, pricing, and milestones in one place — please reach out with any questions before signing.",
      ].join("\n\n");
    case "cost_breakdown_intro":
      if (formatKey === "generic") {
        return "The pricing below covers the scope as discussed.";
      }
      return "The pricing below covers the scope as discussed. Each line item rolls into the total sale price.";
    case "cost_breakdown_footer":
      return DEFAULT_COST_BREAKDOWN_FOOTER;
    case "payment_schedule_intro":
      if (formatKey === "generic") {
        return "Payments are due at the milestones below. The total equals the sale price on the cost breakdown.";
      }
      return "Payments tracked against project milestones. The total below equals the sale price on the cost breakdown.";
    case "payment_schedule_footer":
      return DEFAULT_PAYMENT_SCHEDULE_FOOTER;
    case "intent_to_purchase_preamble":
      return [
        "By signing below, the customer indicates their intent to proceed with the scope and pricing described in this proposal package.",
        "This intent is not a legal contract — a separate purchase agreement will be issued for signature once both parties confirm scope and schedule.",
      ].join(" ");
    case "intent_to_purchase_signature_block":
      // The default signature block is a single paragraph; the visual
      // sig-line scaffolding lives in the PDF section itself.
      return "Please print, sign, and date both signature blocks (customer and company representative). Return a copy to your sales contact to begin scheduling.";
  }
}
